import React, { createContext, useContext, useEffect, useState } from 'react';
import { ROUTE_DENIED } from 'constants/routerPath';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';
import { getInstanceSettingsByApp } from 'api/admin';
import { APP_IDS } from 'constants/apps';

const defaultCANparkRoleContextValue: {
	isAdmin: boolean;
	setIsAdmin: React.Dispatch<React.SetStateAction<boolean>>;
	isLimited: boolean;
} = {
	isAdmin: false,
	setIsAdmin: () => {},
	isLimited: false,
};

const CANparkRoleContext = createContext(defaultCANparkRoleContextValue);

export const useCANparkRoleContext = () => useContext(CANparkRoleContext);

interface CANparkRoleProviderProps {
	children: React.ReactNode;
}

const adminRoles = ['']; // canparks ticket app has no admin roles yet.
const allowedRoles = ['can_park_ticketapp_v3_user'];
export const CANparkRoleProvider = ({ children }: CANparkRoleProviderProps) => {
	const { userInfo } = useSelector(selectUserInfo); // USE THIS TO SET ADMIN STATUS

	const [isAdmin, setIsAdmin] = useState(false); // Initialize isAdmin
	// Default to limited until we confirm otherwise (fail-closed): a missing
	// flag, an in-flight fetch, or a failed fetch all mean limited.
	const [isLimited, setIsLimited] = useState(true);
	const navigate = useNavigate();
	const value = {
		isAdmin,
		setIsAdmin,
		isLimited,
	};

	useEffect(() => {
		if (!userInfo.contactId) return;
		// Check if user has access first:
		const isAllowed = userInfo.roles.some((role: any) =>
			allowedRoles.includes(role),
		);
		const _isAdmin = userInfo.roles.some((role: any) =>
			adminRoles.includes(role),
		);
		if (!isAllowed) {
			navigate(ROUTE_DENIED);
		}
		setIsAdmin(_isAdmin);
	}, [navigate, userInfo]);

	// Per-instance CANpark feature flag. Limited mode (AI plate scanning + CSV
	// export hidden) is the default; full functionality is opt-in and requires
	// an explicit is_limited = 'false' row. A missing row or a failed fetch
	// leaves the instance limited.
	useEffect(() => {
		let cancelled = false;
		const loadLimitedFlag = async () => {
			try {
				const settings = await getInstanceSettingsByApp(APP_IDS.CANPARK);
				const flag = settings.find((s) => s.key === 'is_limited');
				if (!cancelled) {
					setIsLimited(flag?.value !== 'false');
				}
			} catch {
				// On failure, stay limited (fail-closed): we can't confirm full access.
				if (!cancelled) setIsLimited(true);
			}
		};
		loadLimitedFlag();
		return () => {
			cancelled = true;
		};
	}, []);

	return (
		<CANparkRoleContext.Provider value={value}>
			{children}
		</CANparkRoleContext.Provider>
	);
};
